import React, { useState, useEffect, useRef } from 'react';
import styles from './InspectionItemText.module.scss';
import Button from '../../Button';
import ImageDropzone from '../../image_uploads/ImageDropzone';
import {
  openFailureModal,
  openFullPageImageModal,
} from '../../../graphql/cache/modal';
import classNames from 'classnames';
import { Form } from 'react-bootstrap';
import UserSelector from '../../UserSelector';
import SignatureCanvas from '../../SignatureCanvas';
import { Text } from '../../typography';
import TextInputTable from '../../tables/TextInputTable';
import RiskMatrix from '../../RiskMatrix';
import {
  statusType,
  getExtension,
  inputTypes,
} from '../../../utilities/inspection';
import findFileIcon from '../../../utilities/files';
import { Spinner } from 'react-bootstrap';
import UserMultiSelector from '../../UserMultiSelector';
import { useInspection } from '../../../hooks/offline-hooks/createInspectionhook';
import ImageWrapper from '../../offline-wrapper-functions/image-wrapper';
import { useModal } from '../../../providers/ModalProvider';
import { modals } from '../../../providers/modals';
import { useTranslation } from 'react-i18next';
import {
  keys,
  getTranslationKey,
} from '../../../utilities/translator/translation_keys.js';
import MultiSelect from '../../forms/MultiSelect.jsx';
import {
  DateTimePicker,
  LocalizationProvider,
  TimePicker,
  MobileDateTimePicker,
} from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { dateOptions } from '../../../utilities/inspection';
import { useIsMobile } from '../../../hooks/misc.js';
import SpeechToText from '../../SpeechToText.jsx';
import { InputAdornment } from '@mui/material';
import { CalendarIcon } from '@mui/x-date-pickers';
import { downloadFile } from '../../../utilities/inspection';
import { customColors, sanitizeArray } from '../../../utilities/misc.js';
import DropdownCombo from '../../dropdowns/DropdownCombo.jsx';
import { useWorkspace } from '../../../providers/WorkspaceProvider.jsx';
import { dropdownTypes } from '../../dropdowns/dropdown.js';
import InternalFileSelection from '../../image_uploads/InternalFileSelection.jsx';
import Spacer from '../../typography/Spacer.jsx';
import Select from 'react-select';
import Icon from '../../Icon.jsx';
import { useOnlineStatus } from '../../../hooks/offline-hooks/offline-misc.js';
import { useFileUpload } from '../../../hooks/offline-hooks/addFile.js';
import RiskRegistrySelector from '../../../pages/risk_registry/RiskRegistrySelector.jsx';

export default function InspectionItemInput({
  inputType,
  inputValue,
  setInputValue,
  onSubmit,
  title,
  description,
  additionalData,
  inspectionId,
  questionId,
  item,
  isRecording,
  setIsRecording,
  isEditing,
  editingComponent,
  inspectionType,
  refetchCPA,
  setIsEditing,
  workspaceId,
  assetId,
  isDraft,
  preview = false,
  question,
  prefillCandidates,
}) {
  const { openModal, openConfirmationModal } = useModal();
  const { t } = useTranslation();
  const {
    deleteInspectionItemUsersResults,
    addInspectionItemUsersResults,
    updateInspectionTestResults,
  } = useInspection();
  const { addInspectionTestResults } = useInspection();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const dateFormat = dateOptions[inputType];
  const { workspaceVehicles } = useWorkspace();
  const online = useOnlineStatus();
  const { saveFileToFiles } = useFileUpload();
  const { questionOptions } = question;

  const DateTimePickerComponent =
    isMobile && inputType === inputTypes.dateTime
      ? MobileDateTimePicker
      : DateTimePicker;

  const inputRef = useRef(null);
  useEffect(() => {
    if (isEditing && inputRef.current && inputValue) {
      inputRef.current.focus();
    }
  }, [isEditing, inputValue]);

  const pickerStyles = isMobile
    ? {
        textField: {
          sx: { minWidth: '100%' },
          InputProps: {
            endAdornment: (
              <InputAdornment
                sx={{
                  color: '#979797',
                }}
                position="end"
              >
                <CalendarIcon />
              </InputAdornment>
            ),
          },
        },
      }
    : {
        textField: {
          sx: {
            minWidth: '50%',
            marginTop: '0.5rem',
            backgroundColor: 'white',
          },
        },
      };

  const [numberPass, setNumberPass] = useState(null);

  useEffect(() => {
    if (inputType === inputTypes.number) {
      const limits = additionalData?.split('|') || [null, null];
      const min = limits[0] !== '' ? parseFloat(limits[0]) : null;
      const max = limits[1] !== '' ? parseFloat(limits[1]) : null;
      const value = parseFloat(inputValue);
      let pass = true;
      if (min !== null && !isNaN(min) && value < min) {
        pass = false;
      }
      if (max !== null && !isNaN(max) && value > max) {
        pass = false;
      }
      setNumberPass(pass);
    }
  }, [additionalData, inputType, inputValue]);

  switch (inputType) {
    case inputTypes.gps:
      return (
        <div className={styles.flexRow}>
          <Text color={!!item?.value ? 'secondary' : 'primary'} noMargin>
            {item?.value || t(keys.assessments.CLICK_GPS)}
          </Text>
          <Icon
            hover={!loading}
            style={{
              color: loading ? customColors.SECONDARY : customColors.BLUE,
            }}
            onClick={() => {
              if (loading) return;
              setLoading(true);
              navigator.geolocation.getCurrentPosition(
                (loc) => {
                  const lat = loc.coords.latitude.toFixed(4);
                  const lon = loc.coords.longitude.toFixed(4);
                  onSubmit({
                    value: `lat: ${lat}, long: ${lon}`,
                  });
                  setLoading(false);
                },
                (err) => {
                  console.error({ err });
                  setLoading(false);
                },
              );
            }}
          >
            add_location_alt
          </Icon>
        </div>
      );
    case inputTypes.dropdownSingle:
    case inputTypes.dropdownMulti: {
      const isSingle = inputType === inputTypes.dropdownSingle;
      const selectOptions = additionalData
        ?.split('|')
        ?.map((a) => ({ label: a, value: a }));
      const selected = !!inputValue
        ? inputValue?.split('|').map((v) => ({ label: v, value: v }))
        : [];
      return (
        <Select
          isMulti={!isSingle}
          closeMenuOnSelect={isSingle}
          options={selectOptions}
          menuPortalTarget={document.body}
          defaultValue={selected}
          onChange={(newValue) => {
            if (isSingle) {
              onSubmit({ value: newValue.value });
            } else {
              const value = newValue?.map((v) => v.value)?.join('|');
              onSubmit({ value });
            }
          }}
        />
      );
    }
    case inputTypes.asset:
      return (
        <div>
          <DropdownCombo
            type={dropdownTypes.ASSET}
            items={workspaceVehicles}
            selected={item?.vehicle}
            preventStateChange
            onChange={(vehicle) =>
              onSubmit({ value: 'ASSET', vehicleId: vehicle?.id })
            }
            onRemove={() => onSubmit({ value: '', vehicleId: null })}
          />
        </div>
      );
    case inputTypes.multiUser:
      return (
        <div className={styles.multiUser}>
          <UserMultiSelector
            disableSelectAll
            selected={item?.users}
            type={!!workspaceId ? 'workspace' : 'all'}
            onUserAdded={async (user) => {
              let inspectionItemId = item?.id;
              if (!item?.id) {
                const { data } = await addInspectionTestResults({
                  inspectionId,
                  questionId,
                  value: 'USERS',
                  failed: item?.failed || false,
                  status: item?.status || statusType.pass,
                  failNotes: item?.failNotes,
                  failImage: item?.failImage,
                  userId: item?.userId,
                  additional: item?.additional,
                  inputType,
                });

                inspectionItemId = data.addInspectionItem.id;
              } else {
                await onSubmit({ value: 'USERS' });
              }

              await addInspectionItemUsersResults(
                user.id,
                inspectionId,
                questionId,
                inspectionItemId,
              );
              setIsEditing(false);
            }}
            onUserRemoved={async (user) => {
              await deleteInspectionItemUsersResults(
                user.id,
                inspectionId,
                questionId,
                item?.id,
              );
              if (item?.users?.length === 1) await onSubmit({ value: '' });
            }}
          />
        </div>
      );
    case inputTypes.multiImage:
      return (
        <div style={{ marginBottom: '10px' }}>
          {loading ? (
            <Spinner
              className={styles.spinner}
              animation="border"
              variant="primary"
            />
          ) : (
            <div className={styles.multiImage}>
              {inputValue &&
                inputValue?.split('|').map((image, index) => (
                  <div key={`${item?.id}-${index}`}>
                    <ImageWrapper
                      alt="uploaded"
                      className={classNames(styles.image, styles.hover)}
                      image={image}
                      onClick={(e) =>
                        openFullPageImageModal({
                          image: {
                            imageUrl: e.target.src,
                            description: t(keys.common.IMAGE),
                          },
                        })
                      }
                    />
                    <div className={styles.deleteIconContainer}>
                      <Icon
                        className={styles.imgDelete}
                        baseClassName="material-icons-outlined"
                        onClick={() =>
                          openConfirmationModal({
                            title: t(keys.action.DELETE_VARIABLE, {
                              variable: t(keys.common.IMAGE),
                            }),
                            description: t(keys.action.DELETE_CONFIRMATION, {
                              variable: t(keys.common.IMAGE),
                            }),
                            variant: 'warning',
                            onSubmit: () => {
                              const images = item.value?.split('|');
                              const newImages = images.filter(
                                (_, idx) => idx !== index,
                              );
                              const newString = newImages.join('|');
                              updateInspectionTestResults({
                                inspectionId: inspectionId,
                                itemId: item.id,
                                value: newString,
                                failed: false,
                                status: 'PASS',
                              });
                              setInputValue(newString);
                            },
                          })
                        }
                      >
                        delete
                      </Icon>
                    </div>
                  </div>
                ))}
            </div>
          )}
          <ImageDropzone
            title={title}
            onLoading={() => setLoading(true)}
            onSubmit={(url) => {
              setLoading(true);
              const newValue = inputValue ? `${inputValue}|${url}` : `${url}`;
              setInputValue(newValue);
              onSubmit({ value: newValue });
              setLoading(false);
            }}
          />
        </div>
      );
    case inputTypes.file:
      return loading ? (
        <Spinner
          className={styles.spinner}
          animation="border"
          variant="primary"
        />
      ) : (
        <div className={styles.flexRow}>
          <Button
            value={t(keys.action.UPLOAD_VARIABLE, {
              variable: t(keys.common.FILE),
            })}
            icon="backup"
            onClick={() => {
              openModal({
                modalName: modals.fileUpload,
                variables: {
                  assessment: true,
                  simple: true,
                  onSubmit: (file) => {
                    const { url, name } = file;
                    onSubmit({ value: url, additional: name });
                    questionOptions?.saveToAsset &&
                      assetId &&
                      saveFileToFiles({
                        file: {
                          ...file,
                          vehicleId: assetId,
                          downloadAllowed: true,
                        },
                      });
                    setLoading(false);
                  },
                  onLoading: () => {
                    setLoading(true);
                  },
                },
              });
            }}
          />
          {online && (
            <>
              <Text noMargin>or</Text>
              <InternalFileSelection
                onSubmit={(file) => {
                  const { url, name } = file;
                  onSubmit({ value: url, additional: name });
                  questionOptions?.saveToAsset &&
                    assetId &&
                    saveFileToFiles({
                      file: {
                        ...file,
                        vehicleId: assetId,
                        downloadAllowed: true,
                      },
                    });
                  setLoading(false);
                }}
              />
            </>
          )}
        </div>
      );
    case inputTypes.multiFile:
      return (
        <div className={styles.multiFileDisplay}>
          {loading ? (
            <Spinner
              className={styles.spinner}
              animation="border"
              variant="primary"
            />
          ) : additionalData ? (
            additionalData?.split('|').map((additional, i) => (
              <div
                className={styles.fileDisplay}
                key={`name-${additional} + ${i}`}
                style={{ marginBottom: '1.5rem' }}
                onClick={() =>
                  openConfirmationModal({
                    title: t(keys.action.DOWNLOAD_VARIABLE, {
                      variable: additional,
                    }),
                    description: t(keys.action.DOWNLOAD_CONFIRMATION, {
                      variable: additional,
                    }),
                    variant: 'warning',
                    onSubmit: () => {
                      const url = item?.value?.split('|')[i];
                      downloadFile(url, additional);
                    },
                  })
                }
              >
                <div className={styles.card}>
                  <img
                    className={styles.fileIcon}
                    alt={`${additional} file`}
                    src={findFileIcon(
                      getExtension(
                        inputValue?.includes('|')
                          ? inputValue?.split('|')[i]
                          : inputValue,
                      ),
                    )}
                  />
                  <div>
                    <Text noMargin weight="semiBold">
                      {additional}
                    </Text>
                  </div>
                </div>
                <Icon
                  className={styles.imgDelete}
                  baseClassName="material-icons-outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    openConfirmationModal({
                      title: t(keys.action.DELETE_VARIABLE, {
                        variable: t(keys.common.FILE),
                      }),
                      description: t(keys.action.DELETE_CONFIRMATION, {
                        variable: t(keys.common.FILE),
                      }),
                      variant: 'warning',
                      onSubmit: () => {
                        const values = item?.value?.split('|') || [];
                        const additionalValues =
                          item?.additional?.split('|') || [];
                        const newValue = values
                          .filter((_, idx) => idx !== i)
                          .join('|');
                        const newAdditional = additionalValues
                          .filter((_, idx) => idx !== i)
                          .join('|');
                        updateInspectionTestResults({
                          inspectionId: inspectionId,
                          itemId: item.id,
                          value: newValue,
                          additional: newAdditional,
                          failed: false,
                          status: 'PASS',
                        });
                        setInputValue(newValue);
                      },
                    });
                  }}
                >
                  delete
                </Icon>
                <Spacer />
              </div>
            ))
          ) : null}
          <div className={styles.flexRow}>
            <Button
              value={t(keys.action.UPLOAD_VARIABLE, {
                variable: t(keys.common.FILE),
              })}
              disabled={loading}
              icon="backup"
              onClick={() => {
                openModal({
                  modalName: modals.fileUpload,
                  variables: {
                    onSubmit: (file) => {
                      const { url, name } = file;
                      const newValue = !!item?.value
                        ? `${item.value || inputValue}|${url}`
                        : url;
                      setInputValue(newValue);
                      setLoading(false);
                      onSubmit({ value: newValue, additional: name });
                      questionOptions?.saveToAsset &&
                        assetId &&
                        saveFileToFiles({
                          file: {
                            ...file,
                            vehicleId: assetId,
                            downloadAllowed: true,
                          },
                        });
                    },
                    onLoading: () => {
                      setLoading(true);
                    },
                    previousAdditional: additionalData,
                    previousValue: inputValue,
                    simple: true,
                    assessment: true,
                  },
                });
              }}
            />
            <Text noMargin>or</Text>
            <InternalFileSelection
              clear={true}
              onSubmit={(file) => {
                const { url, name } = file;
                const newValue = !!item?.value
                  ? `${item.value || inputValue}|${url}`
                  : url;
                setInputValue(newValue);
                setLoading(false);
                openConfirmationModal({
                  title: 'Upload Document',
                  variant: 'warning',
                  description: `Upload file ${name}?`,
                  onSubmit: () => {
                    onSubmit({
                      value: newValue,
                      additional: !!item?.additional
                        ? `${item.additional || additionalData}|${name}`
                        : name,
                    });
                    questionOptions?.saveToAsset &&
                      assetId &&
                      saveFileToFiles({
                        file: {
                          ...file,
                          vehicleId: assetId,
                          downloadAllowed: true,
                        },
                      });
                  },
                });
              }}
            />
          </div>
        </div>
      );
    case inputTypes.multiText:
      return (
        <div className={styles.inputContainer}>
          <Form.Control
            ref={item?.id === editingComponent ? inputRef : undefined}
            as="textarea"
            className={styles.textInput}
            onChange={(e) => {
              setInputValue(e.target.value);
            }}
            onBlur={() => {
              onSubmit({ value: inputValue });
            }}
            placeholder={t(keys.action.ENTER, {
              variable: t(keys.templates.TEXT),
            })}
            value={inputValue || ''}
            rows={5}
          />
        </div>
      );
    case inputTypes.matrix:
      return (
        <RiskMatrix
          impact={item?.impact}
          probability={item?.probability}
          onChange={(impact, probability) => {
            onSubmit({ value: 'MATRIX', impact, probability });
          }}
        />
      );
    case inputTypes.time:
      return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <TimePicker
            onChange={(newValue) => {
              setInputValue(newValue?.format(dateFormat.format));
            }}
            onAccept={() => onSubmit({ value: inputValue })}
            closeOnSelect={false}
            format={dateFormat.format}
            timeSteps={dateFormat.timeSteps}
            value={
              inputValue ? new moment(inputValue, dateFormat.format) : null
            }
            slotProps={pickerStyles}
            label={t(keys.action.SELECT_VARIABLE, {
              variable: dateFormat.title,
            })}
          />
        </LocalizationProvider>
      );
    case inputTypes.date:
    case inputTypes.dateTime:
      return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DateTimePickerComponent
            fullWidth
            onChange={(newValue) => {
              setInputValue(newValue?.format(dateFormat.format));
            }}
            onAccept={() => onSubmit({ value: inputValue })}
            closeOnSelect={false}
            value={
              inputValue ? new moment(inputValue, dateFormat.format) : null
            }
            slotProps={pickerStyles}
            format={dateFormat.format}
            inputFormat={dateFormat.format}
            views={dateFormat.views}
            timeSteps={dateFormat.timeSteps}
            label={t(keys.action.SELECT_VARIABLE, {
              variable: dateFormat.title,
            })}
          />
        </LocalizationProvider>
      );
    case inputTypes.multiSignature:
      return (
        <div className={styles.multiSignatureDisplay}>
          {inputValue
            ? inputValue?.split('|')?.map((sig, i) => (
                <div key={sig} className={styles.signature}>
                  <Text
                    key={additionalData?.split('|')[i]}
                    color="accentPrimary"
                    className={styles.textResponse}
                  >
                    {additionalData?.split('|')[i]}
                  </Text>
                  <ImageWrapper
                    alt="signature4"
                    className={styles.displayImage}
                    image={sig}
                  />
                </div>
              ))
            : null}
          <SignatureCanvas
            originalAdditional={additionalData}
            onSubmit={(newSignature, failed, status, additional) => {
              const newValue = inputValue
                ? `${inputValue}|${newSignature}`
                : newSignature;
              setInputValue(newValue);
              onSubmit({ value: newValue, failed, status, additional });
            }}
          />
        </div>
      );
    case inputTypes.signature:
      return (
        <div>
          <SignatureCanvas
            originalAdditional={additionalData}
            onSubmit={(newValue, failed, status, additional) =>
              onSubmit({ value: newValue, failed, status, additional })
            }
          />
        </div>
      );
    case inputTypes.table:
      return (
        <div
          style={{
            margin: 'auto',
          }}
        >
          <TextInputTable
            key={`${inspectionId}-${questionId}`}
            headers={additionalData?.split('|')}
            setInputValue={setInputValue}
            inputValue={inputValue}
            onChange={(rows) => {
              onSubmit({ value: rows.join('|') });
            }}
            title={title}
            prefillCandidates={prefillCandidates}
          />
        </div>
      );
    case inputTypes.selectChecklist:
    case inputTypes.multiSelect:
      const multiOptions = additionalData?.split('|');
      const isChecklist = inputType === inputTypes.selectChecklist;
      return (
        <MultiSelect
          vertical={isChecklist}
          title={''}
          options={multiOptions}
          values={item?.value?.split('|') || []}
          onChange={(newValue) => {
            const value = sanitizeArray(newValue);
            onSubmit({ value });
          }}
        />
      );
    case inputTypes.select:
      const singleOptions = additionalData?.split('|') ?? [];
      return singleOptions?.map((o, index) => (
        <Form.Check
          inline
          label={o}
          name={`single-select-${item?.id}`}
          type={'radio'}
          id={inputType + title + index + description}
          checked={item?.value === o}
          value={o}
          key={inputType + title + index + description}
          onChange={(e) => {
            onSubmit({ value: e.target.value });
          }}
        />
      ));
    case inputTypes.user:
      return (
        <UserSelector
          className={styles.avatarBox}
          type={!!workspaceId ? 'workspace' : 'all'}
          selected={inputValue.user}
          onSelectionChanged={(user) => {
            setInputValue({
              userId: user ? user.id : null,
              user: user,
            });
            if (user?.id) {
              onSubmit({ value: user.firstName, userId: user.id });
            }
            setInputValue('');
          }}
        />
      );
    case inputTypes.test:
      return (
        <TestInput
          preview={preview}
          inspectionType={inspectionType}
          title={title}
          description={description}
          onSubmit={onSubmit}
          additional={additionalData ? additionalData?.split('|') : null}
          inspectionId={inspectionId}
          questionId={questionId}
          t={t}
          refetchCPA={refetchCPA}
          assetId={assetId}
        />
      );
    case inputTypes.image:
      return (
        <div className={styles.textInput}>
          {loading ? (
            <Spinner
              className={styles.spinner}
              animation="border"
              variant="primary"
            />
          ) : (
            <ImageDropzone
              title={title}
              onLoading={() => setLoading(true)}
              onSubmit={(url) => {
                setLoading(true);
                onSubmit({ value: url });
                setLoading(false);
              }}
            />
          )}
        </div>
      );
    case inputTypes.text:
      return (
        <div className={styles.inputContainer}>
          <Form
            onSubmit={(e) => e.preventDefault()}
            onChange={(e) => {
              setInputValue(e.target.value);
            }}
            className={styles.textInput}
            onBlur={() => {
              onSubmit({ value: inputValue });
            }}
          >
            <input
              ref={item?.id === editingComponent ? inputRef : undefined}
              className={styles.input}
              value={inputValue || ''}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={t(keys.action.ENTER, {
                variable: t(keys.templates.TEXT),
              })}
            />
          </Form>
        </div>
      );
    case inputTypes.audio:
      return loading ? (
        <Spinner variant="primary" animation="border" />
      ) : (
        <div>
          <SpeechToText
            isRecording={isRecording}
            setIsRecording={setIsRecording}
            transcript={inputValue}
            setTranscript={setInputValue}
            isAssessmentQuestion
            setLoading={setLoading}
            onSubmit={(transcript, URL) => {
              onSubmit({ value: transcript, additional: URL });
            }}
          />
          <br />
        </div>
      );
    case inputTypes.number:
      return (
        <div className={styles.inputContainer}>
          <Form.Control
            type="number"
            ref={item?.id === editingComponent ? inputRef : undefined}
            className={styles.textInput}
            onSubmit={(e) => e.preventDefault()}
            onChange={(e) => {
              setInputValue(e.target.value);
            }}
            isValid={numberPass === true && inputValue}
            isInvalid={numberPass === false && inputValue}
            onBlur={() => {
              onSubmit({
                value: inputValue,
                additional: item?.additional,
                failed: !numberPass,
                status: numberPass ? statusType.pass : statusType.fail,
              });
            }}
            placeholder={t(keys.action.ENTER, {
              variable: t(keys.templates.NUMBER),
            })}
            value={inputValue}
          />
        </div>
      );
    case inputTypes.message:
      return null;
    case inputTypes.riskRegistry:
      return (
        <RiskRegistrySelector
          hideTitle
          isEditing={isDraft}
          onSubmit={(e) =>
            onSubmit({ value: e?.map((v) => v?.value)?.join(',') ?? '' })
          }
          modelType={'inspection'}
          modelId={parseInt(inspectionId)}
        />
      );
    default:
      return (
        <Text size="sm" color="secondaryLight" textAlign="center" noMargin>
          {t(keys.assessments.INVALID_QUESTION_TYPE)}
        </Text>
      );
  }
}

const TestInput = ({
  onSubmit,
  title,
  description,
  additional,
  t,
  inspectionType,
  questionId,
  inspectionId,
  refetchCPA,
  assetId,
  preview,
}) => {
  const { openModal } = useModal();
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const handleSubmit = (args) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    Promise.resolve(onSubmit(args)).finally(() => {
      submittingRef.current = false;
      setSubmitting(false);
    });
  };

  return (
    <div className={styles.testButtonContainer}>
      <Button
        disabled={submitting}
        variant="success"
        value={
          additional
            ? t(getTranslationKey(additional[0], 'assessments')) ||
              additional[0]
            : `${t(keys.assessments.PASS)}`
        }
        size="sm"
        onClick={() =>
          handleSubmit({
            value: statusType.pass,
            status: statusType.pass,
            failed: false,
          })
        }
      />
      <Button
        disabled={submitting}
        variant="danger"
        value={
          additional
            ? t(getTranslationKey(additional[1], 'assessments')) ||
              additional[1]
            : `${t(keys.assessments.FAIL)}`
        }
        size="sm"
        onClick={() => {
          if (preview || submittingRef.current) return;
          inspectionType === 'AUDIT'
            ? openModal({
                modalName: modals.CPA,
                variables: {
                  title,
                  questionId,
                  inspectionId,
                  onSubmit: ({ failNotes, userId }) => {
                    handleSubmit({
                      value: statusType.fail,
                      failed: true,
                      status: statusType.fail,
                      failNotes,
                      userId,
                    });
                    refetchCPA();
                  },
                },
              })
            : openFailureModal({
                questionId,
                title,
                description,
                assetId,
                onSubmit: (notes, imageUrl, assigneeId) => {
                  handleSubmit({
                    value: statusType.fail,
                    userId: assigneeId,
                    failed: true,
                    status: statusType.fail,
                    failNotes: notes,
                    failImage: imageUrl,
                    questionId,
                  });
                },
              });
        }}
      />
      <Button
        disabled={submitting}
        className={additional?.length === 2 && styles.hide}
        variant="secondary"
        value={additional ? additional[2] : 'N/A'}
        size="sm"
        onClick={() =>
          handleSubmit({
            value: statusType.notApplicable,
            failed: false,
            status: statusType.notApplicable,
          })
        }
      />
    </div>
  );
};
